import { useTranslation } from 'react-i18next';
import Icon from '../ui/Icon';
import Reveal from '../ui/Reveal';
import PhotoSlot from './PhotoSlot';
import together from '../../assets/careers/story-together.webp';
import proud from '../../assets/careers/story-proud.webp';
import learning from '../../assets/careers/story-learning.webp';

const BENEFITS = [
  { key: 'growth', icon: 'growth' },
  { key: 'training', icon: 'cap' },
  { key: 'culture', icon: 'heartHands' },
  { key: 'benefits', icon: 'badge' },
];

// Photos are pre-cropped to the tile's 4/3.6 ratio (src/assets/careers).
const STORIES = [
  { key: 'together', src: together },
  { key: 'proud', src: proud },
  { key: 'learning', src: learning },
];

// Two panels sharing a row, divided by a hairline: why the job is worth
// having on the left, and who you would be working with on the right.
export default function WhyJoinAndLife() {
  const { t } = useTranslation();

  return (
    <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 md:py-20 lg:px-8">
      <div className="grid gap-10 lg:grid-cols-2 lg:gap-14 lg:divide-x lg:divide-primary-100">
        {/* ---------- why join ---------- */}
        <div>
          <Reveal>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary-600">
                {t('careers.whyEyebrow')}
              </span>
              <span className="h-px w-8 bg-primary-300" />
            </div>
            <h2 className="mt-3 font-serif text-3xl font-bold leading-tight text-[#2b1a12] md:text-4xl">
              {t('careers.whyTitleLead')}
              <br />
              <span className="text-primary-600">{t('careers.whyTitleAccent')}</span>
            </h2>
          </Reveal>

          <div className="mt-7 grid grid-cols-2 gap-4 sm:grid-cols-4">
            {BENEFITS.map((b, i) => (
              <Reveal key={b.key} delay={i * 0.07} className="h-full">
                <div className="flex h-full flex-col items-center rounded-xl border border-gray-100 bg-white p-4 text-center shadow-sm transition-shadow hover:shadow-md">
                  <Icon name={b.icon} className="h-8 w-8 text-primary-500" strokeWidth={1.5} />
                  <h3 className="mt-3 text-sm font-bold leading-snug text-gray-900">
                    {t(`careers.benefits.${b.key}.title`)}
                  </h3>
                  <p className="mt-1.5 text-xs leading-relaxed text-gray-500">
                    {t(`careers.benefits.${b.key}.body`)}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* ---------- life at ampera ---------- */}
        <div className="lg:pl-14">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary-600">
                    {t('careers.lifeEyebrow')}
                  </span>
                  <span className="h-px w-8 bg-primary-300" />
                </div>
                <h2 className="mt-3 font-serif text-3xl font-bold leading-tight text-[#2b1a12] md:text-4xl">
                  {t('careers.lifeTitleLead')}
                  <br />
                  <span className="text-primary-600">{t('careers.lifeTitleAccent')}</span>
                </h2>
              </div>
              <a
                href="#positions"
                className="inline-flex items-center gap-2 rounded-lg border border-primary-300 px-4 py-2 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-50"
              >
                {t('careers.viewStories')}
                <Icon name="arrowRight" className="h-4 w-4" />
              </a>
            </div>
          </Reveal>

          <div className="mt-7 grid grid-cols-3 gap-4">
            {STORIES.map(({ key, src }, i) => (
              <Reveal key={key} delay={i * 0.08}>
                <figure>
                  <PhotoSlot
                    className="aspect-[4/3.6]"
                    src={src}
                    alt={t(`careers.stories.${key}.title`)}
                    hint={t('careers.photoHint')}
                  />
                  <figcaption className="mt-2.5">
                    <div className="text-sm font-bold text-gray-900">
                      {t(`careers.stories.${key}.title`)}
                    </div>
                    <p className="mt-0.5 text-xs leading-relaxed text-gray-500">
                      {t(`careers.stories.${key}.body`)}
                    </p>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
